import { BaseModalProps, Modal } from '@/components/Modal';
import { Button, Input, Text } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import { DatabaseContract } from '@/lib/api/movidesk';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { contractsApi, UpdateContractDto } from '@/lib/api/contracts';
import { useContracts } from '@/hooks/useContracts';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';

const contractSyncSchema = (t: any) =>
  z
    .object({
      contracts: z.array(
        z.object({
          id: z.string(),
          startDate: z.date({
            required_error: t('contracts.form.startDateRequired'),
            invalid_type_error: t('contracts.form.invalidDate'),
          }),
          endDate: z.date({
            required_error: t('contracts.form.endDateRequired'),
            invalid_type_error: t('contracts.form.invalidDate'),
          }),
          contractedHours: z
            .string()
            .min(1, t('contracts.form.contractedHoursRequired'))
            .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
              message: t('contracts.form.invalidHours'),
            }),
        })
      ),
    })
    .refine(
      (data) => {
        return data.contracts.every((contract) => contract.endDate > contract.startDate);
      },
      {
        message: t('contracts.form.endDateAfterStartDate'),
        path: ['contracts'],
      }
    );

type ContractSyncFormData = z.infer<ReturnType<typeof contractSyncSchema>>;

export interface SyncContractsModalProps extends BaseModalProps {
  data: DatabaseContract[];
}

export function SyncContractsModal({ open, onOpenChange, data }: SyncContractsModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { refreshContracts } = useContracts();

  const schema = contractSyncSchema(t);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    reset,
    formState: { errors },
  } = useForm<ContractSyncFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      contracts: [],
    },
  });

  useEffect(() => {
    if (data.length > 0) {
      reset({
        contracts: data.map((contract) => ({
          id: contract.id,
          startDate: contract.startDate ? new Date(contract.startDate) : undefined,
          endDate: contract.endDate ? new Date(contract.endDate) : undefined,
          contractedHours: contract.contractedHours ? contract.contractedHours.toString() : '',
        })),
      });
    }
  }, [data, reset]);

  const { fields } = useFieldArray({
    control,
    name: 'contracts',
  });

  const handleCancel = () => {
    onOpenChange(false);
  };

  const onSubmit = async (formData: ContractSyncFormData) => {
    try {
      setIsSubmitting(true);

      const updatePromises = formData.contracts.map((contractData) => {
        const updateData: UpdateContractDto = {
          startDate: contractData.startDate,
          endDate: contractData.endDate,
          contractedHours: contractData.contractedHours,
        };
        return contractsApi.update(contractData.id, updateData);
      });

      await Promise.all(updatePromises);

      toast.success(t('contracts.syncModal.success'));
      await refreshContracts();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('contracts.syncModal.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('contracts.syncModal.title')}
      width={900}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="gap-md flex flex-col">
        {fields.map((field, index) => {
          const contract = data[index];
          const fieldError = errors.contracts?.[index];

          return (
            <>
              <div key={field.id} className="gap-2xs grid grid-cols-4 items-center">
                <div>
                  <Text variant="label" strong>
                    {contract.company.name}
                  </Text>
                  <Text size="xs" className="text-neutral-muted">
                    {contract.number}
                  </Text>
                </div>
                <DatePicker
                  label={t('contracts.form.startDate')}
                  placeholder={t('common.select')}
                  value={watch(`contracts.${index}.startDate`)}
                  onChange={(date) => setValue(`contracts.${index}.startDate`, date as Date)}
                  error={fieldError?.startDate?.message}
                  inputDisabled={isSubmitting}
                />
                <DatePicker
                  label={t('contracts.form.endDate')}
                  placeholder={t('common.select')}
                  value={watch(`contracts.${index}.endDate`)}
                  onChange={(date) => setValue(`contracts.${index}.endDate`, date as Date)}
                  error={fieldError?.endDate?.message}
                  inputDisabled={isSubmitting}
                  disabled={
                    watch(`contracts.${index}.startDate`)
                      ? { before: watch(`contracts.${index}.startDate`) }
                      : undefined
                  }
                />
                <Input
                  {...register(`contracts.${index}.contractedHours`)}
                  type="number"
                  label={t('contracts.form.contractedHours')}
                  placeholder={t('contracts.form.contractedHoursPlaceholder')}
                  error={fieldError?.contractedHours?.message}
                  disabled={isSubmitting}
                />
              </div>
              {index < fields.length - 1 && <hr className="border-neutral-subtle" />}
            </>
          );
        })}

        {errors.contracts && typeof errors.contracts.message === 'string' && (
          <Text size="sm" className="text-red-500">
            {errors.contracts.message}
          </Text>
        )}

        <footer className="gap-sm mt-2xl flex self-end">
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {t('common.import')}
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
